/**
 * leads.service.ts
 *
 * Typed API call wrappers for the leads domain.
 * All methods use the shared apiClient (Axios instance with JWT interceptor).
 * Returns unwrapped data from the response envelope — callers never see Axios wrappers.
 */
import { apiClient } from './apiClient';
import type { PaginatedResponse, ApiResponse } from '@/types/api.types';

// ─── Domain types ─────────────────────────────────────────────────────────────

export type LeadStatus = 'new' | 'contacted' | 'qualified' | 'converted' | 'dead' | 'spam' | 'trash';

/** Tab filter matching old PHP system, plus the mixed source list used by V5. */
export type LeadTab = 'all' | 'new' | 'done' | 'bad' | 'sms' | 'forms';
export type LeadKind = 'email' | 'sms' | 'form';

/**
 * Which marketing message produced a lead that came in through a request-info
 * link. Null (or absent, on API builds that predate it) for every other lead.
 *
 * `kind: 'link'` is a bare link — pasted on a website, say — so there is no
 * campaign or step to name, only the resource. `deleted` means the campaign or
 * automation has since been deleted; its name is still the one it had.
 */
export interface LeadMarketingSource {
  kind: 'campaign' | 'automation' | 'link';
  channel: 'email' | 'sms' | null;
  campaignName: string | null;
  automationName: string | null;
  stepOrder: number | null;
  stepName: string | null;
  resourceName: string;
  /** Filed through "Not {name}?" by someone the link was forwarded to. */
  referral: boolean;
  deleted: boolean;
}

export interface Lead {
  rowKey?: string;
  sourceId?: string;
  leadKind?: LeadKind;
  leadKindLabel?: string;
  leadId: number;
  accountId: number;
  patientId?: number | null;
  firstName: string;
  middleName: string | null;
  lastName: string;
  maidenName: string | null;
  fullName: string;
  email: string | null;
  phone: string | null;
  cellPhone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  gender: string | null;
  birthDate: string | null;
  heightFeet: number | null;
  heightInches: number | null;
  weight: number | null;
  smsOptIn: boolean;
  emailOptIn: boolean;
  marketingSms?: boolean;
  marketingEmail?: boolean;
  referralSource: string | null;
  referralCategory: string | null;
  referralSpecific: string | null;
  referralFirstName: string | null;
  referralLastName: string | null;
  referralOther: string | null;
  leadSourceId: number | null;
  /** Specific lead source (referral_level2 = contacts.handlerID); prefills the intake "Lead Source" select. */
  referralSourceId: number | null;
  leadSourceName: string | null;
  doctorId: number | null;
  doctorName: string | null;
  coordinatorId: number | null;
  coordinatorName: string | null;
  facilityId: number | null;
  facilityName: string | null;
  languageId: number | null;
  languageName: string | null;
  planTypeId: number | null;
  planTypeName: string | null;
  stageName?: string | null;
  procedure1Id: number | null;
  procedure1Name: string | null;
  procedure2Id: number | null;
  procedure2Name: string | null;
  procedure3Id: number | null;
  procedure3Name: string | null;
  timeframe: string | null;
  financing: string | null;
  interestLevel: number | null;
  ballparkGiven: string | null;
  ballparkOpinion: string | null;
  callerIdNumber: string | null;
  dateAdded: string;
  lastContact: string | null;
  status: LeadStatus;
  assignedToId: number | null;
  /** Active contacts_assign.roleID when the lead is assigned to a group rather than a user. */
  assignedRoleId: number | null;
  assignedToName: string | null;
  notes: string | null;
  procedureInterest: string | null;
  message: string | null;
  /** Pre-delimiter portion of `message`; safe to render inline. Always present on show(); may be absent on list rows. */
  messagePreview?: string | null;
  /** Post-delimiter portion of `message` — raw original email HTML. Rendered into an iframe srcdoc. */
  messageRaw?: string | null;
  /** True when the lead's message contains legacy's RAW CONTENT delimiter. */
  hasRawContent?: boolean;
  subject: string | null;
  badReason: string | null;
  siteReferrer: string | null;
  referUrl: string | null;
  /** Legacy c.reference — "Practice Referral" field. */
  practiceReferral?: string | null;
  /** Legacy c.referKeywords — "Search Terms" field. */
  referKeywords?: string | null;
  planId: number | null;
  /** Lead source logo filename (legacy referral_level2.image, with extension stripped). */
  leadSourceImage?: string | null;
  /** "Converted by X" / "Marked Spam by X" / "Marked Trash by X" user full name. */
  convertedByName?: string | null;
  /** Populated when legacy encoded badReason as "{patientID}|{planID}" (merged lead). */
  mergedPatientId?: number | null;
  mergedPlanId?: number | null;
  /** Full name of the patient this lead was merged into. */
  mergedPatientName?: string | null;
  /** Custom intake-form Q&A rows (contacts_record). */
  contactsRecord?: Array<{ label: string; value: string }>;
  /** File uploads attached to the web lead (contacts_attachments). */
  contactsAttachments?: Array<{ id: number; filename: string; downloadUrl: string }>;
  /** Origin of the lead (e.g. web form name, landing page) */
  leadFrom: string | null;
  formOrigin: string | null;
  /** UTM tracking fields */
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  /** Elapsed time string since lead received, e.g. "2h 15m" */
  timer: string | null;
  formType?: string | null;
  sourceTable?: string | null;
  formId?: number | null;
  /** Set on list rows and on show() for request-info leads; see LeadMarketingSource. */
  marketingSource?: LeadMarketingSource | null;
  /**
   * The completed form this lead carries, on the detail payload only. Null when
   * the lead has no completed form; absent on API builds that predate it.
   */
  eform?: LeadEform | null;
}

/**
 * A completed form (surveys_responses row) a lead carries. `ownerPatientId` is
 * set once the form is on a patient's chart; while it is null, staff can attach
 * it with `attachLeadForm` (patientForms.service).
 */
export interface LeadEform {
  responseId: number;
  title: string;
  dateCompleted: string;
  ownerPatientId: number | null;
  ownerName: string | null;
}

export interface CreateLeadPayload {
  firstName: string;
  lastName: string;
  middleName?: string;
  maidenName?: string;
  email?: string;
  phone?: string;
  cellPhone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  gender?: string;
  birthDate?: string;
  heightFeet?: number;
  heightInches?: number;
  weight?: number;
  smsOptIn?: boolean;
  emailOptIn?: boolean;
  referralSource?: string;
  referralCategory?: string;
  referralSpecific?: string;
  referralFirstName?: string;
  referralLastName?: string;
  referralOther?: string;
  leadSourceId?: number;
  doctorId?: number;
  coordinatorId?: number;
  facilityId?: number;
  languageId?: number;
  planTypeId?: number;
  procedure1Id?: number;
  procedure2Id?: number;
  procedure3Id?: number;
  timeframe?: string;
  financing?: string;
  interestLevel?: number;
  ballparkGiven?: string;
  ballparkOpinion?: string;
  callerIdNumber?: string;
  assignedToId?: number;
  notes?: string;
  procedureInterest?: string;
}

export type UpdateLeadPayload = Partial<CreateLeadPayload> & {
  status?: LeadStatus;
  lastContact?: string;
  badReason?: string;
};

export interface LeadListParams {
  search?: string;
  filtersArray?: string;
  /** `key:0;key:1` — 1 marks that filter negative. Companion to filtersArray. */
  filterPlus?: string;
  status?: LeadStatus;
  tab?: LeadTab;
  assignedToId?: number;
  leadSourceId?: number;
  planTypeId?: number;
  facilityId?: number;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  perPage?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  /** Backend implementation flag — 'v2' enables the compound-UNION fast path
   *  for tab=all. No-op for other tabs. */
  engine?: 'v2';
}

export interface LeadCounts {
  new: number;
  done: number;
  bad: number;
  sms: number;
  forms: number;
  total: number;
}

// ─── Service methods ──────────────────────────────────────────────────────────

/**
 * Fetch a paginated list of leads, optionally filtered.
 *
 * When `tab === 'all'`, automatically opts into the v2 compound-UNION
 * backend path. v1 fans out into 5 separate JOIN-heavy queries which time
 * out on large tenants — v2 issues one wrapped UNION + cached counts.
 * Other tabs already run a single query, so v2 is a no-op for them.
 */
export function listLeads(params?: LeadListParams): Promise<PaginatedResponse<Lead>> {
  const effective: LeadListParams | undefined =
    params?.tab === 'all' && !params.engine
      ? { ...params, engine: 'v2' }
      : params;
  return apiClient
    .get<PaginatedResponse<Lead>>('/leads', { params: effective })
    .then((r) => r.data);
}

/**
 * Fetch the full lead record by ID.
 */
export function getLead(id: number): Promise<Lead> {
  return apiClient
    .get<ApiResponse<Lead>>(`/leads/${id}`)
    .then((r) => r.data.data);
}

/**
 * Create a new lead.
 */
export function createLead(data: CreateLeadPayload): Promise<Lead> {
  return apiClient
    .post<ApiResponse<Lead>>('/leads', data)
    .then((r) => r.data.data);
}

/**
 * Update an existing lead (partial update supported).
 */
export function updateLead(id: number, data: UpdateLeadPayload): Promise<Lead> {
  return apiClient
    .put<ApiResponse<Lead>>(`/leads/${id}`, data)
    .then((r) => r.data.data);
}

/**
 * Assign a lead to a user or group with legacy parity.
 *
 * `value` follows legacy `<option value>` semantics 1:1:
 *   - `""`           → unassign: closes the active row, inserts none, no notif
 *                      (legacy lead-assign-user.php ignored empty; V5 does not)
 *   - `"<userID>"`   → assign to that user (closes previous active row, fires notif typeID=21)
 *   - `"role_<id>"`  → assign to that role/group (fans notif to every active role member)
 */
export function assignLead(id: number, value: string): Promise<Lead> {
  return apiClient
    .put<ApiResponse<Lead>>(`/leads/${id}/assign`, { value })
    .then((r) => r.data.data);
}

/**
 * Delete a lead.
 */
export function deleteLead(id: number): Promise<void> {
  return apiClient.delete(`/leads/${id}`).then(() => undefined);
}

/**
 * Convert a lead to a patient and create an initial plan.
 * Returns the newly created patientId and planId.
 */
export function convertToPatient(leadId: number): Promise<{ patientId: number; planId: number }> {
  console.log('[lead-convert] convertToPatient() → POST /leads/' + leadId + '/convert');
  return apiClient
    .post<ApiResponse<{ patientId: number; planId: number }>>(`/leads/${leadId}/convert`)
    .then((r) => {
      console.log('[lead-convert] convertToPatient() response', r.data.data);
      return r.data.data;
    });
}

/**
 * Fetch lead counts by tab (new/done/bad/sms/forms).
 */
export function getLeadCounts(params?: Omit<LeadListParams, 'tab' | 'page' | 'perPage' | 'sort' | 'order'>): Promise<LeadCounts> {
  return apiClient
    .get<ApiResponse<LeadCounts>>('/leads/counts', { params })
    .then((r) => r.data.data);
}

/**
 * Mark a lead as bad with a reason.
 */
export function markBad(leadId: number, reason: string): Promise<Lead> {
  return apiClient
    .post<ApiResponse<Lead>>(`/leads/${leadId}/mark-bad`, { reason })
    .then((r) => r.data.data);
}

export interface PatientMatch {
  patientId: number;
  fullName: string;
  phone: string | null;
  email: string | null;
  matchReasons: Array<'name' | 'lastName' | 'firstName' | 'email' | 'phone'>;
}

/**
 * Search for similar patients by name, email, and/or phone (deduplication check).
 */
export function searchSimilarNames(
  firstName: string,
  lastName: string,
  email?: string,
  phone?: string,
): Promise<PatientMatch[]> {
  return apiClient
    .get<ApiResponse<PatientMatch[]>>('/leads/similar-names', {
      params: { firstName, lastName, email, phone },
    })
    .then((r) => r.data.data);
}

/**
 * A master patient record (cross-account identity) that EXACTLY matches the
 * email or phone typed into the Add New Patient form.
 * Backend: GET /v5/leads/master-matches (LeadsController::masterMatches).
 *
 * Carries only what the form populates on selection (the card shows the email
 * and phone so staff can confirm the record). The server never returns which
 * other accounts the person is a patient at — staff of one account must not
 * see that.
 */
export interface MasterPatientMatch {
  masterPatientId: number;
  firstName: string;
  middleName: string | null;
  lastName: string;
  maidenName: string | null;
  gender: string | null;
  /** YYYY-MM-DD, null when unknown. */
  birthDate: string | null;
  email: string | null;
  /** Best value for the form's Cell Phone field (cellPhone, else the identity phone minus its dialing code). */
  formCellPhone: string | null;
  /** The cell's dial code ('44'), never a countryID. */
  cellCountryCode: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  emailOptIn: boolean;
  smsOptIn: boolean;
  emailUnsubscribe: boolean;
  smsUnsubscribe: boolean;
  matchReasons: Array<'email' | 'phone'>;
  /** Set when the master already has an active patient in THIS account — use the in-account flow. */
  linkedPatientId: number | null;
  /**
   * Display name of the corporation this account belongs to (master_accounts.title),
   * shown as who the record is shared with. Null when the corp has no title.
   * NOT the other account's name — which accounts the person is a patient at is
   * deliberately never returned.
   */
  masterAccountName: string | null;
}

/**
 * Exact email/phone match against the master patient list for this account's
 * master account. Empty unless something matches exactly (never fuzzy).
 */
export function searchMasterMatches(email?: string, phone?: string): Promise<MasterPatientMatch[]> {
  return apiClient
    .get<ApiResponse<MasterPatientMatch[]>>('/leads/master-matches', {
      params: { email, phone },
    })
    .then((r) => r.data.data ?? []);
}

// ─── Merge to Comm Log (legacy lead_merge_to_comm_log.php parity) ────────────

/**
 * Scored patient candidate for a lead's "Possible Matches" panel.
 * Backend: GET /v5/leads/{id}/match-candidates (LeadsController::matchCandidates).
 * Shape mirrors CommLogController::matchCandidates + adds a `dob` match flag.
 */
export interface LeadMatchCandidate {
  patientId: number;
  displayId: number | string | null;
  firstName: string;
  lastName: string;
  email: string | null;
  cellPhone: string | null;
  homePhone: string | null;
  birthDate: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  matchScore: number;
  matches: {
    firstName: boolean;
    lastName: boolean;
    name: boolean;
    email: boolean;
    phone: boolean;
    dob: boolean;
  };
}

/**
 * Fetch scored patient candidates for the lead's "Possible Matches" panel.
 * Mirrors legacy GetSimilarWebLeadNamesConvert + the per-row scoring at
 * message-lead-display-fs.php L703-934.
 */
export function getLeadMatchCandidates(
  leadId: number,
  leadKind: 'email' | 'sms' = 'email',
): Promise<LeadMatchCandidate[]> {
  return apiClient
    .get<ApiResponse<LeadMatchCandidate[]>>(`/leads/${leadId}/match-candidates`, {
      params: { leadKind },
    })
    .then((r) => r.data.data ?? []);
}

export interface MergeLeadPayload {
  patientId: number;
  /** 0 = no plan ("None"); plan must belong to the patient. */
  planId?: number;
  /** 0 = not visible to patient (default), 1 = visible (typeID 7/18). */
  mergeCommType?: 0 | 1;
  /** True copies the message into the patient's notes as well. */
  mergeCopyToNote?: boolean;
  /** 'email' (default) or 'sms'. */
  leadKind?: 'email' | 'sms';
}

export interface MergeLeadResult {
  commLogId: number;
  patientId: number;
  planId: number;
  attachmentsCopied: number;
  attachmentsMissing: number;
}

/**
 * Merge a lead into the comm_log under a patient (and optionally a plan).
 * Exact parity port of legacy /lead_merge_to_comm_log.php.
 */
export function mergeLeadToCommLog(
  leadId: number,
  payload: MergeLeadPayload,
): Promise<MergeLeadResult> {
  return apiClient
    .post<ApiResponse<MergeLeadResult>>(`/leads/${leadId}/merge-to-comm-log`, payload)
    .then((r) => r.data.data);
}

/**
 * Export leads to an XLSX spreadsheet.
 */
export function exportLeadsCsv(params?: LeadListParams): Promise<Blob> {
  return apiClient
    .get('/leads/export', { params, responseType: 'blob' })
    .then((r) => r.data as Blob);
}
